// Copyright (c) Ping. All rights reserved.
//
// Full-screen celebratory effects (confetti / hearts / fireworks) streamed to
// everyone in the call via the LiveKit data channel. Each effect shows who sent
// it (initials avatar + name). All motion is GPU-composited CSS keyframes on
// transform/opacity — no per-frame JS — and particle params are memoised once
// per instance so re-renders never restart or re-randomise the animation. The
// store caps + auto-expires `effects`, so this layer is always bounded.

import { useEffect, useMemo } from 'react';

import type { CallEffectKind } from '@calls/livekit/callData';
import type { CallEffectView } from '@calls/store/callStore';

const STYLE_ID = 'ping-call-effect-overlay';

function ensureKeyframes(): void {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
@keyframes ping-fx-confetti {
  0%   { transform: translate3d(0,-12vh,0) rotate(0deg); opacity: 0; }
  8%   { opacity: 1; }
  100% { transform: translate3d(var(--dx,0), 112vh, 0) rotate(var(--rot,540deg)); opacity: 1; }
}
@keyframes ping-fx-heart {
  0%   { transform: translate3d(0,18vh,0) scale(0.6); opacity: 0; }
  12%  { opacity: 1; }
  100% { transform: translate3d(var(--dx,0), -96vh, 0) scale(1.1); opacity: 0; }
}
@keyframes ping-fx-spark {
  0%   { transform: translate3d(0,0,0) scale(0.4); opacity: 0; }
  10%  { opacity: 1; }
  100% { transform: translate3d(var(--tx,0), var(--ty,0), 0) scale(1); opacity: 0; }
}
@keyframes ping-fx-label {
  0%   { transform: translateY(8px) scale(0.92); opacity: 0; }
  10%  { transform: translateY(0) scale(1); opacity: 1; }
  82%  { transform: translateY(0) scale(1); opacity: 1; }
  100% { transform: translateY(-6px) scale(0.98); opacity: 0; }
}`;
  document.head.appendChild(el);
}

// Small deterministic PRNG (mulberry32) so each effect instance lays out its
// particles consistently without Math.random churn across re-renders.
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashCode(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h) || 1;
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

const CONFETTI_COLORS = ['#537EFF', '#FF5353', '#FFCB45', '#41921C', '#2CA8D4', '#E8743B'];
const VERB: Record<CallEffectKind, string> = {
  confetti: 'celebrated 🎉',
  hearts: 'sent love 💕',
  fireworks: 'lit it up 🎆',
};

interface Particle {
  readonly left: string;
  readonly delay: string;
  readonly duration: string;
  readonly style: React.CSSProperties;
  readonly content?: string;
}

function buildParticles(kind: CallEffectKind, seed: number): Particle[] {
  const rnd = mulberry32(seed);
  if (kind === 'hearts') {
    return Array.from({ length: 22 }, () => {
      const left = `${rnd() * 100}%`;
      return {
        left,
        delay: `${rnd() * 0.8}s`,
        duration: `${3.4 + rnd() * 1.2}s`,
        content: rnd() > 0.5 ? '❤️' : '💖',
        style: {
          fontSize: 20 + Math.round(rnd() * 18),
          ['--dx' as string]: `${(rnd() - 0.5) * 120}px`,
          animationName: 'ping-fx-heart',
        } as React.CSSProperties,
      };
    });
  }
  if (kind === 'fireworks') {
    // A few bursts; each burst is a ring of sparks radiating from its center.
    const bursts = 5;
    const perBurst = 14;
    const out: Particle[] = [];
    for (let b = 0; b < bursts; b += 1) {
      const cx = 12 + rnd() * 76; // vw
      const cy = 14 + rnd() * 50; // vh
      const burstDelay = rnd() * 1.6;
      const color = CONFETTI_COLORS[Math.floor(rnd() * CONFETTI_COLORS.length)]!;
      for (let i = 0; i < perBurst; i += 1) {
        const ang = (i / perBurst) * Math.PI * 2;
        const dist = 80 + rnd() * 70;
        out.push({
          left: `${cx}vw`,
          delay: `${burstDelay.toFixed(2)}s`,
          duration: `${1 + rnd() * 0.5}s`,
          style: {
            top: `${cy}vh`,
            width: 7,
            height: 7,
            borderRadius: 999,
            background: color,
            boxShadow: `0 0 8px ${color}`,
            ['--tx' as string]: `${Math.cos(ang) * dist}px`,
            ['--ty' as string]: `${Math.sin(ang) * dist}px`,
            animationName: 'ping-fx-spark',
          } as React.CSSProperties,
        });
      }
    }
    return out;
  }
  // confetti
  return Array.from({ length: 46 }, () => {
    const color = CONFETTI_COLORS[Math.floor(rnd() * CONFETTI_COLORS.length)]!;
    return {
      left: `${rnd() * 100}%`,
      delay: `${rnd() * 1.1}s`,
      duration: `${2.6 + rnd() * 1.6}s`,
      style: {
        width: 7 + Math.round(rnd() * 5),
        height: 10 + Math.round(rnd() * 8),
        background: color,
        borderRadius: 2,
        ['--dx' as string]: `${(rnd() - 0.5) * 240}px`,
        ['--rot' as string]: `${360 + Math.round(rnd() * 720)}deg`,
        animationName: 'ping-fx-confetti',
      } as React.CSSProperties,
    };
  });
}

function EffectInstance({ effect }: { effect: CallEffectView }): JSX.Element {
  const particles = useMemo(
    () => buildParticles(effect.effect, hashCode(effect.id)),
    [effect.effect, effect.id],
  );
  const isTop = effect.effect === 'hearts'; // hearts rise from the bottom
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      {particles.map((pcl, i) => (
        <span
          key={i}
          style={{
            position: 'absolute',
            left: pcl.left,
            ...(isTop ? { bottom: 0 } : { top: 0 }),
            willChange: 'transform, opacity',
            animationTimingFunction: 'ease-out',
            animationFillMode: 'forwards',
            animationIterationCount: 1,
            animationDelay: pcl.delay,
            animationDuration: pcl.duration,
            ...pcl.style,
          }}
        >
          {pcl.content}
        </span>
      ))}

      {/* Sender label card */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: 28,
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 14px 6px 6px',
          borderRadius: 999,
          background: 'rgba(0,0,0,0.6)',
          border: '1px solid rgba(255,255,255,0.1)',
          animation: 'ping-fx-label 5s ease-out forwards',
        }}
      >
        <span
          style={{
            width: 26,
            height: 26,
            borderRadius: 999,
            background: '#537EFF',
            color: '#fff',
            fontSize: 11,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {initialsOf(effect.fromName)}
        </span>
        <span style={{ fontSize: 13, color: '#F5F6F8', fontWeight: 600 }}>
          {effect.fromName} {VERB[effect.effect]}
        </span>
      </div>
    </div>
  );
}

export function CallEffectOverlay({ effects }: { effects: readonly CallEffectView[] }): JSX.Element | null {
  useEffect(() => {
    ensureKeyframes();
  }, []);
  if (effects.length === 0) return null;
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 7 }}>
      {effects.map((e) => (
        <EffectInstance key={e.id} effect={e} />
      ))}
    </div>
  );
}
